'use client';

/**
 * SendLoader: envelope → paper plane → search → launch → animated green tick circle.
 *
 * One continuous, premium loader for the "Send" action. Mount it when the CTA is pressed,
 * flip `done` to true when the request resolves, and it finishes with the launch,
 * animatedly transitions into a green tick circle, and calls `onLaunched`.
 *
 *   {sending && <SendLoader done={sent} onLaunched={() => setSending(false)} />}
 *
 * - Zero external dependencies (React only).
 * - Driven by a single requestAnimationFrame loop directly mutating DOM attributes for 60fps smoothness.
 * - Honours prefers-reduced-motion.
 * - Screen-reader accessible (role="status", aria-live="polite").
 */

import React, { useEffect, useRef, useState } from 'react';

type Pt = [number, number];

export interface SendLoaderColors {
  ink: string;
  green: string;
  paper: string;
  wash: string;
  muted: string;
  circleBg?: string;
}

export interface SendLoaderCaptions {
  preparing: string;
  searching: string;
  sent: string;
}

export interface SendLoaderProps {
  /** Set to true when the send request has finished. The plane launches once minDurationMs has elapsed. */
  done?: boolean;
  /** Rendered size in px. Default 168. */
  size?: number;
  /** Minimum time before launch so the sequence never flashes by on a fast response. Default 3200ms. */
  minDurationMs?: number;
  /** Text under the animation. Pass `false` to hide it. */
  captions?: false | SendLoaderCaptions;
  /** Override brand colours, e.g. { green: '#16a34a' } */
  colors?: Partial<SendLoaderColors>;
  /** Called once when the launch & checkmark animation has finished. */
  onLaunched?: () => void;
  className?: string;
  /** Design review / tests: render the frame at this time (seconds) instead of animating. */
  debugT?: number;
}

/* Eextramile brand tokens */
const DEFAULT_COLORS: SendLoaderColors = {
  ink: '#0c0c1a',
  green: '#16a34a',
  paper: '#fbfcfa',
  wash: 'rgba(22, 163, 74, 0.12)',
  muted: '#64617a',
  circleBg: 'rgba(22, 163, 74, 0.08)',
};

const DEFAULT_CAPTIONS: SendLoaderCaptions = {
  preparing: 'Preparing your email…',
  searching: 'Finding the right recruiters…',
  sent: 'Workflow triggered',
};

/* ---------- timeline (seconds) ---------- */
const INTRO = 1.45; // envelope fold + morph to plane
const PERIOD = 1.8; // search sweep period
const TOTAL_LAUNCH_TIME = 1.4; // plane takeoff + green tick circle entrance
const HEADING = -28; // plane heading in degrees (nose up-right)

/* ---------- geometry in a 200 × 200 viewBox ---------- */
const L_ENV: Pt[] = [[44, 76], [156, 76], [156, 132], [44, 132]];
const U_OPEN: Pt[] = [[44, 76], [156, 76], [100, 40], [100, 40]];
const U_CLOSED: Pt[] = [[44, 76], [156, 76], [100, 110], [100, 110]];
const SEAL_C: Pt = [100, 110];
const G_ENV: Pt[] = [[100, 102], [108, 110], [100, 118], [92, 110]];

// Paper plane geometry
const U_PL: Pt[] = [[40, 52], [176, 96], [92, 100], [92, 100]];
const L_PL: Pt[] = [[92, 100], [176, 96], [120, 116], [60, 138]];
const G_PL: Pt[] = [[92, 100], [126, 106], [76, 130], [92, 100]];

// Speed lines trailing behind
const LINE_Y = [70, 86, 101, 116, 132];
const LINE_GREEN = [true, false, false, true, false];

// Sparkles
const SPARKS = [
  { x: 146, y: 52, green: false, o: 0 },
  { x: 26, y: 62, green: true, o: 0.35 },
  { x: 160, y: 146, green: false, o: 0.7 },
];

// Tick circle constants
const TICK_RADIUS = 36;
const TICK_CIRCUMFERENCE = 2 * Math.PI * TICK_RADIUS; // ~226.19
const CHECK_PATH_LENGTH = 44; // Total length of the checkmark path

/* ---------- interpolation & easing helpers ---------- */
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const seg = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const easeIn = (t: number) => t * t * t;
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const backOut = (t: number, s = 1.70158) => 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);

const mix = (A: Pt[], B: Pt[], t: number): Pt[] =>
  A.map((p, i) => [lerp(p[0], B[i][0], t), lerp(p[1], B[i][1], t)] as Pt);
const str = (P: Pt[]) => P.map((p) => `${p[0].toFixed(2)},${p[1].toFixed(2)}`).join(' ');
const scaleAbout = (P: Pt[], c: Pt, s: number): Pt[] =>
  P.map((p) => [c[0] + (p[0] - c[0]) * s, c[1] + (p[1] - c[1]) * s] as Pt);

function hexToRgb(h: string): [number, number, number] {
  const m = h.replace('#', '');
  const n = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  return [parseInt(n.slice(0, 2), 16), parseInt(n.slice(2, 4), 16), parseInt(n.slice(4, 6), 16)];
}
function mixColor(a: string, b: string, t: number) {
  if (!a.startsWith('#') || !b.startsWith('#')) return t < 0.5 ? a : b;
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return `rgb(${Math.round(lerp(A[0], B[0], t))},${Math.round(lerp(A[1], B[1], t))},${Math.round(lerp(A[2], B[2], t))})`;
}

interface Pose {
  group: string;
  groupOp: number;
  halo: string;
  haloOp: number;
  shadowRx: number;
  shadowOp: number;
  U: string;
  L: string;
  Lfill: string;
  G: string;
  magT: string;
  magOp: number;
  lines: { x1: number; x2: number; y: number; op: number }[];
  sparks: { t: string; op: number }[];

  // Tick Circle & Checkmark
  tickGroupT: string;
  tickGroupOp: number;
  tickCircleOffset: number;
  tickCircleFillOp: number;
  tickCheckOffset: number;
  tickHaloT: string;
  tickHaloOp: number;
}

/**
 * Pure evaluation function: computes all scene attributes at time t.
 * @param t Seconds since start.
 * @param u Launch sequence progress (0..1) or -1 if not yet launching.
 */
function pose(t: number, u: number, c: SendLoaderColors): Pose {
  const launching = u >= 0;
  const enter = easeOut(seg(t, 0, 0.45));
  const flap = easeInOut(seg(t, 0.05, 0.55));
  const seal = backOut(seg(t, 0.5, 0.72));
  const morph = easeInOut(seg(t, 0.9, INTRO));
  const ts = Math.max(0, t - INTRO);
  const settle = seg(t, INTRO, INTRO + 0.35);
  const ang = (ts * Math.PI * 2) / PERIOD;

  // Plane morph shapes
  const uEnv = mix(U_OPEN, U_CLOSED, flap);
  const U = mix(uEnv, U_PL, morph);
  const L = mix(L_ENV, L_PL, morph);
  const G = mix(scaleAbout(G_ENV, SEAL_C, Math.max(0, seal)), G_PL, morph);

  // Flight kinematics
  const hop = -10 * Math.sin(Math.PI * seg(t, 0.9, INTRO));
  const bob = launching ? 0 : Math.sin(ang) * 3 * settle;
  const wobble = launching ? 0 : Math.sin(ang + 1) * 1.1 * settle;
  let rot = lerp(0, HEADING, morph) + wobble;
  let sc = lerp(0.86, 1, enter) * lerp(1, 1.06, morph);
  let dx = 0;
  let dy = hop + bob + lerp(6, 0, enter);
  let groupOp = enter;
  let stretch = 0;
  let magScale = backOut(seg(t, INTRO + 0.05, INTRO + 0.35));

  // Launch trajectory (plane sweeps up-right)
  if (launching) {
    // 0.00..0.15 anticipation pull-back
    const a = seg(u, 0, 0.15);
    // 0.15..0.55 swift takeoff along heading vector
    const v = seg(u, 0.15, 0.55);
    const off = -12 * easeOut(a) + easeIn(v) * 280;
    const rad = (-HEADING * Math.PI) / 180;
    dx += Math.cos(rad) * off;
    dy -= Math.sin(rad) * off;
    sc *= lerp(1, 0.95, easeOut(a)) * lerp(1, 0.6, v);
    // Smoothly dissolve plane as it exits
    groupOp *= 1 - seg(v, 0.5, 0.95);
    stretch = easeIn(seg(u, 0.1, 0.55));
    magScale *= 1 - easeIn(seg(u, 0, 0.15));
  }

  const group = `translate(${dx.toFixed(2)} ${dy.toFixed(2)}) translate(100 100) rotate(${rot.toFixed(2)}) scale(${sc.toFixed(4)}) translate(-100 -100)`;

  // Speed lines
  const lineW = seg(t, INTRO - 0.15, INTRO + 0.25) * (launching ? 1 - seg(u, 0.45, 0.6) : 1);
  const lines = LINE_Y.map((y, i) => {
    const phase = (((ts * 0.95 + i * 0.21) % 1) + 1) % 1;
    const len = 12 * (1 + 2.8 * stretch);
    const x1 = 40 - phase * 34 * (1 + stretch);
    return { x1, x2: x1 - len, y, op: Math.sin(Math.PI * phase) * lineW };
  });

  // Sparkles
  const sparks = SPARKS.map((s) => {
    const k = Math.abs(Math.sin(ang * 0.5 + s.o * Math.PI * 2));
    const w = seg(t, INTRO + 0.1 + s.o * 0.3, INTRO + 0.5 + s.o * 0.3) * (launching ? 1 - seg(u, 0, 0.25) : 1);
    return { t: `translate(${s.x} ${s.y}) scale(${(0.45 + 0.55 * k).toFixed(3)})`, op: (0.35 + 0.65 * k) * w };
  });

  // Magnifier sweep
  const mx = 118 + 22 * Math.sin(ang);
  const my = 95 + 9 * Math.sin(2 * ang + 0.6);

  // Background halo & shadow
  const haloBase = 0.96 + 0.04 * Math.sin(ang) * settle;
  const haloScale = launching ? lerp(1, 1.4, easeOut(seg(u, 0, 0.5))) : haloBase * lerp(0.8, 1, enter);
  const haloOp = launching ? 1 - seg(u, 0, 0.5) : enter;
  const lift = launching ? 1 - easeIn(seg(u, 0.05, 0.5)) : 1;
  const shadowRx = 34 * lerp(1, 0.9, Math.abs(Math.sin(ang / 2))) * lift * lerp(0.8, 1, enter);
  const shadowOp = 0.08 * enter * lift;

  // ── Green Tick Circle & Checkmark Animation ──────────────────────────────────
  // Begins seamlessly as the plane reaches takeoff (u = 0.35 .. 1.0)
  let tickGroupOp = 0;
  let tickScale = 0;
  let tickCircleOffset = TICK_CIRCUMFERENCE;
  let tickCircleFillOp = 0;
  let tickCheckOffset = CHECK_PATH_LENGTH;
  let tickHaloScale = 0;
  let tickHaloOp = 0;

  if (launching && u >= 0.32) {
    // 0.32 .. 0.70: Circle scale and draw-in
    const cProg = seg(u, 0.32, 0.68);
    tickGroupOp = easeOut(seg(u, 0.32, 0.45));
    tickScale = backOut(cProg, 1.4);

    // Perimeter stroke draws clockwise
    tickCircleOffset = TICK_CIRCUMFERENCE * (1 - easeOut(cProg));
    tickCircleFillOp = easeOut(seg(u, 0.45, 0.75));

    // 0.55 .. 0.92: Checkmark springs & draws in
    const kProg = seg(u, 0.52, 0.88);
    tickCheckOffset = CHECK_PATH_LENGTH * (1 - easeOut(kProg));

    // Soft emerald confirmation pulse
    const pProg = seg(u, 0.58, 1.0);
    tickHaloScale = lerp(0.8, 1.45, easeOut(pProg));
    tickHaloOp = (1 - easeIn(pProg)) * 0.45;
  }

  const tickGroupT = `translate(100 100) scale(${Math.max(0, tickScale).toFixed(4)}) translate(-100 -100)`;
  const tickHaloT = `translate(100 100) scale(${Math.max(0, tickHaloScale).toFixed(4)}) translate(-100 -100)`;

  return {
    group,
    groupOp,
    halo: `translate(100 100) scale(${haloScale.toFixed(4)}) translate(-100 -100)`,
    haloOp,
    shadowRx,
    shadowOp,
    U: str(U),
    L: str(L),
    Lfill: mixColor(c.paper, c.ink, seg(morph, 0.5, 0.9)),
    G: str(G),
    magT: `translate(${mx.toFixed(2)} ${my.toFixed(2)}) scale(${Math.max(0, magScale).toFixed(4)})`,
    magOp: magScale > 0.01 ? 1 : 0,
    lines,
    sparks,

    tickGroupT,
    tickGroupOp,
    tickCircleOffset,
    tickCircleFillOp,
    tickCheckOffset,
    tickHaloT,
    tickHaloOp,
  };
}

export default function SendLoader({
  done = false,
  size = 168,
  minDurationMs = 3200,
  captions = DEFAULT_CAPTIONS,
  colors,
  onLaunched,
  className,
  debugT,
}: SendLoaderProps) {
  const c: SendLoaderColors = { ...DEFAULT_COLORS, ...colors };
  const cap = captions === false ? null : captions;
  const els = useRef<Record<string, SVGElement | null>>({});
  const r = (k: string) => (el: SVGElement | null) => {
    els.current[k] = el;
  };
  const doneRef = useRef(done);
  doneRef.current = done;
  const launchedRef = useRef(onLaunched);
  launchedRef.current = onLaunched;
  const colorsRef = useRef(c);
  colorsRef.current = c;

  const [phase, setPhase] = useState<'preparing' | 'searching' | 'sent'>('preparing');
  const [gone, setGone] = useState(false);

  // Initial frame
  const [p0] = useState(() => pose(debugT ?? 0.75, -1, c));

  const paint = (p: Pose) => {
    const set = (k: string, a: Record<string, string | number>) => {
      const el = els.current[k];
      if (!el) return;
      for (const n in a) el.setAttribute(n, String(a[n]));
    };

    // Plane & Envelope
    set('group', { transform: p.group, opacity: p.groupOp });
    set('halo', { transform: p.halo, opacity: p.haloOp });
    set('shadow', { rx: p.shadowRx, 'fill-opacity': p.shadowOp });
    set('U', { points: p.U });
    set('L', { points: p.L, fill: p.Lfill });
    set('G', { points: p.G });
    set('mag', { transform: p.magT, opacity: p.magOp });
    p.lines.forEach((l, i) => set(`l${i}`, { x1: l.x1, x2: l.x2, y1: l.y, y2: l.y, opacity: l.op }));
    p.sparks.forEach((s, i) => set(`s${i}`, { transform: s.t, opacity: s.op }));

    // Green Tick Circle & Checkmark
    set('tickGroup', { transform: p.tickGroupT, opacity: p.tickGroupOp });
    set('tickCircle', {
      'stroke-dashoffset': p.tickCircleOffset,
      'fill-opacity': p.tickCircleFillOp,
    });
    set('tickCheck', { 'stroke-dashoffset': p.tickCheckOffset });
    set('tickHalo', { transform: p.tickHaloT, opacity: p.tickHaloOp });
  };

  useEffect(() => {
    if (debugT !== undefined) {
      const lt = debugT - minDurationMs / 1000;
      paint(pose(debugT, lt >= 0 ? clamp01(lt / TOTAL_LAUNCH_TIME) : -1, colorsRef.current));
      return;
    }

    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let alive = true;

    if (reduce) {
      paint(pose(INTRO + 0.6, -1, colorsRef.current));
      setPhase('searching');
      const check = () => {
        if (!alive) return;
        if (doneRef.current) {
          setPhase('sent');
          paint(pose(INTRO + 1, 0.95, colorsRef.current));
          timer = setTimeout(() => {
            setGone(true);
            launchedRef.current?.();
          }, 900);
        } else {
          timer = setTimeout(check, 120);
        }
      };
      timer = setTimeout(check, Math.max(600, minDurationMs / 2));
      return () => {
        alive = false;
        if (timer) clearTimeout(timer);
      };
    }

    const t0 = performance.now();
    let launchStart = -1;
    let lastPhase = 'preparing';

    const tick = (now: number) => {
      if (!alive) return;
      const t = (now - t0) / 1000;
      if (launchStart < 0 && doneRef.current && t >= minDurationMs / 1000) {
        launchStart = now;
      }
      const u = launchStart >= 0 ? clamp01((now - launchStart) / 1000 / TOTAL_LAUNCH_TIME) : -1;

      // Update text phase: 'Workflow triggered' activates smoothly when the green tick starts blooming (u >= 0.38)
      const next = u >= 0.38 ? 'sent' : t >= INTRO ? 'searching' : 'preparing';
      if (next !== lastPhase) {
        lastPhase = next;
        setPhase(next as 'preparing' | 'searching' | 'sent');
      }

      paint(pose(t, u, colorsRef.current));

      if (u >= 1) {
        // Allow a brief graceful pause for the user to register the green tick confirmation
        timer = setTimeout(() => {
          if (!alive) return;
          setGone(true);
          launchedRef.current?.();
        }, 380);
        return;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debugT, minDurationMs]);

  const sw = 4.5;
  const common = { stroke: c.ink, strokeWidth: sw, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  const label = cap ? cap[phase] : phase === 'sent' ? 'Workflow triggered' : 'Sending';

  return (
    <div
      className={className}
      role="status"
      aria-live="polite"
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        overflow: 'visible',
        opacity: gone ? 0 : 1,
        pointerEvents: gone ? 'none' : undefined,
        transition: 'opacity 0.35s ease',
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 200 200"
        fill="none"
        aria-hidden="true"
        style={{ overflow: 'visible' }}
      >
        {/* Soft background halo */}
        <circle ref={r('halo')} cx="100" cy="100" r="74" fill={c.wash} transform={p0.halo} opacity={p0.haloOp} />

        {/* Ground shadow */}
        <ellipse ref={r('shadow')} cx="100" cy="172" rx={p0.shadowRx} ry="5" fill={c.ink} fillOpacity={p0.shadowOp} />

        {/* ── Paper Plane & Envelope Group ── */}
        <g ref={r('group')} transform={p0.group} opacity={p0.groupOp}>
          {/* Speed lines */}
          {LINE_Y.map((y, i) => (
            <line
              key={i}
              ref={r(`l${i}`)}
              x1={p0.lines[i].x1}
              x2={p0.lines[i].x2}
              y1={y}
              y2={y}
              opacity={p0.lines[i].op}
              stroke={LINE_GREEN[i] ? c.green : c.ink}
              strokeWidth={3.2}
              strokeLinecap="round"
            />
          ))}

          {/* Envelope & Plane Morphs */}
          <polygon ref={r('L')} points={p0.L} fill={p0.Lfill} {...common} />
          <polygon ref={r('U')} points={p0.U} fill={c.paper} {...common} />
          <polygon ref={r('G')} points={p0.G} fill={c.green} stroke="none" />

          {/* Sparkles */}
          {SPARKS.map((s, i) => (
            <path
              key={i}
              ref={r(`s${i}`)}
              d="M-5 0H5M0 -5V5"
              transform={p0.sparks[i].t}
              opacity={p0.sparks[i].op}
              stroke={s.green ? c.green : c.ink}
              strokeWidth={3}
              strokeLinecap="round"
            />
          ))}

          {/* Magnifier glass */}
          <g ref={r('mag')} transform={p0.magT} opacity={p0.magOp}>
            <line x1="11.5" y1="11.5" x2="23" y2="23" stroke={c.green} strokeWidth={7} strokeLinecap="round" />
            <circle r="16" fill={c.paper} {...common} />
          </g>
        </g>

        {/* ── Green Tick Circle & Checkmark ── */}
        <circle
          ref={r('tickHalo')}
          cx="100"
          cy="100"
          r="48"
          fill={c.wash}
          transform={p0.tickHaloT}
          opacity={p0.tickHaloOp}
        />

        <g ref={r('tickGroup')} transform={p0.tickGroupT} opacity={p0.tickGroupOp}>
          {/* Outer confirmation circle with stroke dash drawing */}
          <circle
            ref={r('tickCircle')}
            cx="100"
            cy="100"
            r={TICK_RADIUS}
            fill={c.circleBg || 'rgba(22, 163, 74, 0.08)'}
            stroke={c.green}
            strokeWidth={4.5}
            strokeLinecap="round"
            strokeDasharray={TICK_CIRCUMFERENCE}
            strokeDashoffset={p0.tickCircleOffset}
            transform="rotate(-90 100 100)"
          />

          {/* Crisp drawn checkmark */}
          <path
            ref={r('tickCheck')}
            d="M86 101 L96 112 L116 90"
            fill="none"
            stroke={c.green}
            strokeWidth={4.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={CHECK_PATH_LENGTH}
            strokeDashoffset={p0.tickCheckOffset}
          />
        </g>
      </svg>

      {cap ? (
        <span
          style={{
            fontFamily: 'var(--font-ui, Inter, system-ui, -apple-system, "Segoe UI", sans-serif)',
            fontSize: 14,
            lineHeight: 1.4,
            fontWeight: 500,
            color: phase === 'sent' ? c.green : c.muted,
            minHeight: 20,
            transition: 'color 0.3s ease',
          }}
        >
          {label}
        </span>
      ) : (
        <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
          {label}
        </span>
      )}
    </div>
  );
}

/**
 * Overlay wrapper: centres the loader over a frosted backdrop.
 *
 *   <SendLoaderOverlay open={sending} done={sent} onLaunched={() => setSending(false)} />
 */
export function SendLoaderOverlay({
  open,
  ...rest
}: SendLoaderProps & { open: boolean }) {
  if (!open) return null;
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(251, 252, 250, 0.78)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}
    >
      <SendLoader {...rest} />
    </div>
  );
}
